import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { IconButton, Button, Card, Input, Select, Typography } from '@/primer/components';
import { PageColumn } from '@/components/layout';
import { ReservationTimerBanner } from '@/components/ui';
import {
  useVerifyAddress,
  useVerifiedAddresses,
  type CreateUserVerifiedAddressRequest,
} from '@/api';
import {
  addressSchema,
  US_STATES,
  SUPPORTED_COUNTRIES,
  COUNTRY_OPTIONS,
  type AddressFormData,
} from '@/schemas';
import { getFieldError } from '@/utils';

interface Props {
  /** When set, renders in campaign flow: navigates back to campaign address screen on save. */
  campaignId?: number;
  /** When set, pre-fills the form with the address matching this id. */
  addressId?: number;
  /** ISO date string of when the product was reserved — drives the countdown timer. */
  reservedDate?: string;
}

/**
 * Shipping Address screen.
 *
 * Accessible from Settings → Shipping address, or from the campaign participation flow.
 * Collects and verifies a US/CA/GB shipping address then navigates to /settings (when
 * accessed from Settings) or to /campaign/$id/address (when `campaignId` is provided).
 */
export function Address({ campaignId, addressId, reservedDate }: Props) {
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();
  const verifyMutation = useVerifyAddress();
  const { data: addressesResponse } = useVerifiedAddresses();

  const existingAddress = useMemo(() => {
    if (!addressesResponse?.data?.length) return null;
    if (addressId) return addressesResponse.data.find((a) => a.id === addressId) ?? null;
    return addressesResponse.data.at(-1) ?? null;
  }, [addressId, addressesResponse]);

  const handleBack = () => {
    if (!campaignId) {
      navigate({ to: '/settings' });
      return;
    }
    if (addressId) {
      navigate({ to: '/campaign/$id/address', params: { id: campaignId } });
      return;
    }
    navigate({ to: '/campaign/$id/tasks', params: { id: campaignId } });
  };

  const form = useForm({
    defaultValues: {
      firstName: existingAddress?.firstName ?? '',
      lastName: existingAddress?.lastName ?? '',
      street: existingAddress?.street ?? '',
      apartmentNumber: existingAddress?.apartmentNumber ?? '',
      city: existingAddress?.city ?? '',
      subdivision: existingAddress?.subdivision ?? '',
      postalCode: existingAddress?.postalCode ?? '',
      countryCode: (existingAddress?.countryCode ?? 'US') as (typeof SUPPORTED_COUNTRIES)[number],
      phoneNumber: existingAddress?.phoneNumber ?? '',
    } as AddressFormData,
    onSubmit: async ({ value }) => {
      setSubmitError('');
      try {
        const address: CreateUserVerifiedAddressRequest = {
          firstName: value.firstName,
          lastName: value.lastName,
          street: value.street,
          apartmentNumber: value.apartmentNumber || undefined,
          city: value.city,
          subdivision: value.subdivision,
          postalCode: value.postalCode,
          countryCode: value.countryCode,
          phoneNumber: value.phoneNumber,
        };

        await verifyMutation.mutateAsync(address);
        if (campaignId) {
          navigate({ to: '/campaign/$id/address', params: { id: campaignId } });
        } else {
          navigate({ to: '/settings' });
        }
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to verify address');
      }
    },
  });

  return (
    <PageColumn className="py-6 flex flex-col gap-6">
      {campaignId && reservedDate && (
        <ReservationTimerBanner campaignId={campaignId} reservedDate={reservedDate} />
      )}
      <div className="flex items-center gap-3">
        <IconButton icon="chevron-left" onClick={handleBack} aria-label="Go back" />
        <Typography variant="header" size="h1" as="h1">
          Shipping address
        </Typography>
      </div>

      <Card>
        <div className="p-5">
          {submitError && (
            <div className="bg-error border border-error rounded-small p-3 mb-4">
              <Typography variant="body" size="small" color="error">
                {submitError}
              </Typography>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <form.Field name="firstName" validators={{ onBlur: addressSchema.shape.firstName }}>
                {(field) => (
                  <Input
                    label="First Name"
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={
                      field.state.meta.isTouched
                        ? getFieldError(field.state.meta.errors)
                        : undefined
                    }
                    autoComplete="given-name"
                  />
                )}
              </form.Field>

              <form.Field name="lastName" validators={{ onBlur: addressSchema.shape.lastName }}>
                {(field) => (
                  <Input
                    label="Last Name"
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={
                      field.state.meta.isTouched
                        ? getFieldError(field.state.meta.errors)
                        : undefined
                    }
                    autoComplete="family-name"
                  />
                )}
              </form.Field>
            </div>

            <form.Field name="street" validators={{ onBlur: addressSchema.shape.street }}>
              {(field) => (
                <Input
                  label="Street Address"
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="123 Main St"
                  error={
                    field.state.meta.isTouched ? getFieldError(field.state.meta.errors) : undefined
                  }
                  autoComplete="address-line1"
                />
              )}
            </form.Field>

            <form.Field name="apartmentNumber">
              {(field) => (
                <Input
                  label="Apartment Number"
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Apt, Suite, etc. (optional)"
                  autoComplete="address-line2"
                />
              )}
            </form.Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <form.Field name="city" validators={{ onBlur: addressSchema.shape.city }}>
                {(field) => (
                  <Input
                    label="City"
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={
                      field.state.meta.isTouched
                        ? getFieldError(field.state.meta.errors)
                        : undefined
                    }
                    autoComplete="address-level2"
                  />
                )}
              </form.Field>

              <form.Field
                name="subdivision"
                validators={{
                  onBlur: ({ value, fieldApi }) => {
                    const countryCode = fieldApi.form.getFieldValue('countryCode');
                    if (countryCode === 'US') {
                      if (!value) return 'State is required';
                      if (!US_STATES.includes(value as (typeof US_STATES)[number])) {
                        return 'Please select a valid US state';
                      }
                    } else if (!value) {
                      return 'State/Province is required';
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => {
                  const countryCode = form.getFieldValue('countryCode');
                  const isUS = countryCode === 'US';

                  return isUS ? (
                    <Select
                      label="State"
                      id={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      autoComplete="address-level1"
                      error={
                        field.state.meta.isTouched
                          ? getFieldError(field.state.meta.errors)
                          : undefined
                      }
                      placeholder="Select state"
                      options={US_STATES.map((s) => ({ value: s, label: s }))}
                    />
                  ) : (
                    <Input
                      label="State/Province"
                      id={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="State/Province"
                      error={
                        field.state.meta.isTouched
                          ? getFieldError(field.state.meta.errors)
                          : undefined
                      }
                      autoComplete="address-level1"
                    />
                  );
                }}
              </form.Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <form.Field
                name="postalCode"
                validators={{
                  onBlur: ({ value, fieldApi }) => {
                    const countryCode = fieldApi.form.getFieldValue('countryCode');
                    if (!value) return 'Postal code is required';
                    if (countryCode === 'US' && !/^\d{5}(-\d{4})?$/.test(value)) {
                      return 'Please enter a valid ZIP code (e.g., 12345)';
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => {
                  const countryCode = form.getFieldValue('countryCode');
                  const isUS = countryCode === 'US';

                  return (
                    <Input
                      label={isUS ? 'ZIP Code' : 'Postal Code'}
                      id={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder={isUS ? '12345' : 'Postal code'}
                      error={
                        field.state.meta.isTouched
                          ? getFieldError(field.state.meta.errors)
                          : undefined
                      }
                      autoComplete="postal-code"
                    />
                  );
                }}
              </form.Field>

              <form.Field
                name="countryCode"
                validators={{ onBlur: addressSchema.shape.countryCode }}
              >
                {(field) => (
                  <Select
                    label="Country"
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value as (typeof SUPPORTED_COUNTRIES)[number]);
                      form.setFieldValue('subdivision', '');
                    }}
                    onBlur={field.handleBlur}
                    autoComplete="country"
                    error={
                      field.state.meta.isTouched
                        ? getFieldError(field.state.meta.errors)
                        : undefined
                    }
                    options={COUNTRY_OPTIONS}
                  />
                )}
              </form.Field>
            </div>

            <form.Field
              name="phoneNumber"
              validators={{
                onBlur: ({ value }) => {
                  if (!value) return 'Phone number is required';
                  if (!/^\+?[\d\s().-]{10,20}$/.test(value)) {
                    return 'Please enter a valid phone number';
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <Input
                  label="Phone Number"
                  type="tel"
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="(555) 123-4567"
                  error={
                    field.state.meta.isTouched ? getFieldError(field.state.meta.errors) : undefined
                  }
                  autoComplete="tel"
                />
              )}
            </form.Field>

            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  isLoading={isSubmitting || verifyMutation.isPending}
                >
                  Verify & Save Address
                </Button>
              )}
            </form.Subscribe>
          </form>
        </div>
      </Card>
    </PageColumn>
  );
}

export default Address;
